// CFG-IA.A1 (#1536) — Settings → Infrastructure.
//
// Surfaces cloud-credential management (BYOC/AWS) for an established tenant.
// DEPLOY.A1 (#952) shipped the backend + the wizard components, but they were
// reachable ONLY via the unlinked onboarding wizard. This page exposes them
// post-onboarding, TENANT_ADMIN-gated, reusing — not copying — the wizard
// components (D5/D6/D7).
//
// Credential-handling contract:
//   - The full AWS secret NEVER reaches this page. The masked summary reads
//     GET /tenants/:id/credentials/cloud, which returns only a masked key id
//     (`...last4`) + region + validatedAt — no secret leaves the server.
//   - Secret ENTRY happens inside BYOCCredentialsStep, where the secret lives
//     only in that component's transient state behind a PasswordInput. This
//     page never reads, renders, or logs it.
//
// Admin gate is fail-CLOSED, modeled on SettingsTenantBrandingPage: a null /
// failed membership leaves isAdmin false → redirect to /settings, never fall
// through to the credential surface; members issue no credential request.

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import { apiFetch } from '../lib/api';
import Step2InfrastructureMode, { type InfrastructureModeData } from '../components/wizard/steps/Step2InfrastructureMode';
import BYOCCredentialsStep from '../components/wizard/steps/BYOCCredentialsStep';

type Mode = 'BYOC' | 'MANAGED';

// Masked-read shape from GET /tenants/:id/credentials/cloud (credentials.ts).
// maskedKeyId is `...` + last4; never a full secret.
interface CloudAws {
  connected:   boolean;
  maskedKeyId: string | null;
  region:      string | null;
  validatedAt: string | null;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 24,
  marginBottom: 16,
};

const summaryRow: React.CSSProperties = {
  display: 'flex', gap: 12, padding: '6px 0',
  fontFamily: 'var(--font-body)', fontSize: 13,
};
const summaryLabel: React.CSSProperties = {
  width: 130, color: 'var(--text-secondary)', flexShrink: 0,
};

export default function SettingsInfrastructurePage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  // Fail CLOSED — a non-admin OR a null/failed membership redirects to /settings
  // rather than rendering the credential surface (mirrors SettingsTenantBrandingPage).
  useEffect(() => {
    if (!tenantLoading && !isAdmin) navigate('/settings');
  }, [tenantLoading, isAdmin, navigate]);

  // Deployment mode — editable (D6). Seed once from the membership response;
  // after a successful PATCH the local value wins. Rendering of the mode
  // component is gated on mode !== null so its initial selection is correct
  // (the component seeds its internal state from savedData on mount only).
  const [mode, setMode] = useState<Mode | null>(null);
  useEffect(() => {
    if (mode === null && myTenant?.infrastructureMode) {
      setMode(myTenant.infrastructureMode === 'MANAGED' ? 'MANAGED' : 'BYOC');
    }
  }, [mode, myTenant?.infrastructureMode]);

  const [modeSaving, setModeSaving] = useState(false);
  const [modeError, setModeError] = useState('');
  const [modeSavedAt, setModeSavedAt] = useState<number | null>(null);

  // Masked cloud-credential read (BYOC only).
  const [cloud, setCloud] = useState<CloudAws | null>(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Returns the refetch promise so callers (e.g. delete) can await the masked
  // summary refresh before clearing their own pending state.
  const fetchCloud = useCallback(() => {
    if (!tenantId || !isAdmin) return;
    setCloudLoading(true);
    setDeleteError('');
    return apiFetch<{ aws: CloudAws }>(`/tenants/${tenantId}/credentials/cloud`)
      .then(res => setCloud(res.aws))
      .catch(() => setCloud(null))
      .finally(() => setCloudLoading(false));
  }, [tenantId, isAdmin]);

  // Load the masked summary whenever the surface is in BYOC mode.
  useEffect(() => {
    if (isAdmin && mode === 'BYOC' && tenantId) void fetchCloud();
  }, [isAdmin, mode, tenantId, fetchCloud]);

  const handleModeSave = async (data: InfrastructureModeData) => {
    if (!tenantId) return;
    setModeSaving(true);
    setModeError('');
    setModeSavedAt(null);
    try {
      // PATCH /tenants/:id is TENANT_ADMIN-gated server-side and accepts the
      // infrastructureMode enum (updateTenantBody). A non-admin can't reach
      // this handler anyway (the page redirected them).
      await apiFetch(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({ infrastructureMode: data.infrastructureMode }),
      });
      setMode(data.infrastructureMode);
      setModeSavedAt(Date.now());
    } catch (e) {
      setModeError(e instanceof Error ? e.message : 'Failed to update deployment mode');
    } finally {
      setModeSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!tenantId) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await apiFetch(`/tenants/${tenantId}/credentials/cloud/aws`, { method: 'DELETE' });
      // Await the masked-summary refresh so the spinner stays until the UI
      // reflects the removal (no re-enabled button over stale "connected").
      await fetchCloud();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to remove credentials');
    } finally {
      setDeleting(false);
    }
  };

  // Auto-clear the transient "saved" confirmation so it doesn't linger
  // indefinitely after a deployment-mode save (cleaned up on unmount/re-save).
  useEffect(() => {
    if (modeSavedAt === null) return;
    const t = setTimeout(() => setModeSavedAt(null), 3000);
    return () => clearTimeout(t);
  }, [modeSavedAt]);

  // Fail-closed placeholder: non-admin / null membership is being redirected.
  // tenantId is guaranteed once isAdmin (membership loaded), but narrow for TS.
  if (!isAdmin || !tenantId) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p data-testid="infrastructure-redirecting" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
            Redirecting…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }} data-testid="infrastructure-page">
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <div style={{ marginBottom: 16 }}>
          <Link to="/settings" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            ← Back to settings
          </Link>
        </div>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          Infrastructure
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, maxWidth: 640 }}>
          Choose how BluePrint hosts your application and, for Bring Your Own Cloud, manage the cloud account it deploys into. Only tenant admins can view or edit.
        </p>

        <div style={{ maxWidth: 640 }}>
          {/* Deployment mode — editable BYOC/MANAGED toggle (reuses the wizard
              component via its minimal submitLabel/showChangeLaterHint props). */}
          <div style={cardStyle}>
            {mode === null ? (
              <p data-testid="infrastructure-mode-loading" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                Loading deployment mode…
              </p>
            ) : (
              <>
                <Step2InfrastructureMode
                  tenantId={tenantId}
                  savedData={{ infrastructureMode: mode }}
                  submitLabel={modeSaving ? 'Saving…' : 'Save deployment mode'}
                  showChangeLaterHint={false}
                  onStepComplete={(data) => { void handleModeSave(data); }}
                />
                {modeError && (
                  <p data-testid="mode-error" style={{ marginTop: 12, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
                    {modeError}
                  </p>
                )}
                {modeSavedAt && !modeError && (
                  <p data-testid="mode-saved" style={{ marginTop: 12, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--success)' }}>
                    Deployment mode saved.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Cloud credentials — only in BYOC mode. */}
          {mode === 'BYOC' && (
            <>
              <div style={cardStyle}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                  Cloud credentials (AWS)
                </h2>
                {cloudLoading ? (
                  <p data-testid="cloud-loading" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    Loading…
                  </p>
                ) : cloud?.connected ? (
                  <div data-testid="cloud-connected">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--success)' }} />
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>Connected</span>
                    </div>
                    <div style={summaryRow}>
                      <span style={summaryLabel}>Access key ID</span>
                      <span data-testid="cloud-masked-key" style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text)' }}>
                        {cloud.maskedKeyId ?? '—'}
                      </span>
                    </div>
                    <div style={summaryRow}>
                      <span style={summaryLabel}>Region</span>
                      <span style={{ color: 'var(--text)' }}>{cloud.region ?? '—'}</span>
                    </div>
                    <div style={summaryRow}>
                      <span style={summaryLabel}>Validated</span>
                      <span style={{ color: 'var(--text)' }}>
                        {cloud.validatedAt ? new Date(cloud.validatedAt).toLocaleString() : '—'}
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid="cloud-delete"
                      onClick={() => { void handleDelete(); }}
                      disabled={deleting}
                      style={{
                        marginTop: 14, padding: '8px 16px',
                        fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                        borderRadius: 'var(--radius-sm)', border: '1px solid var(--error)',
                        background: 'transparent', color: 'var(--error)',
                        cursor: deleting ? 'not-allowed' : 'pointer', opacity: deleting ? 0.6 : 1,
                      }}
                    >
                      {deleting ? 'Removing…' : 'Remove credentials'}
                    </button>
                    {deleteError && (
                      <p data-testid="cloud-delete-error" style={{ marginTop: 10, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>
                        {deleteError}
                      </p>
                    )}
                  </div>
                ) : (
                  <p data-testid="cloud-not-connected" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                    No cloud credentials connected. Add them below — they are validated against AWS before they are saved.
                  </p>
                )}
              </div>

              {/* Entry / update form. The component validates (POST /cloud/validate)
                  then saves (POST /cloud), keeps the secret in its own transient
                  state behind a PasswordInput, and self-rehydrates its region from
                  the same masked endpoint. On completion we refetch the summary. */}
              <div style={cardStyle}>
                <BYOCCredentialsStep
                  tenantId={tenantId}
                  onStepComplete={() => { void fetchCloud(); }}
                />
              </div>
            </>
          )}

          {/* Managed mode — no credentials required. */}
          {mode === 'MANAGED' && (
            <div style={cardStyle} data-testid="managed-no-creds">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                Cloud credentials
              </h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                BluePrint hosts your infrastructure in Managed mode — no cloud credentials are required. Switch to <strong>Bring Your Own Cloud</strong> above to connect an AWS account.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
